/**
 * Centralized Language System
 * 
 * This module provides a scalable internationalization (i18n) system
 * that supports multiple languages beyond just English and German.
 * 
 * To add a new language:
 * 1. Create a new language file (e.g., FR.js for French)
 * 2. Export it following the same structure as EN.js/DE.js
 * 3. Import and register it in the 'languages' object below
 * 4. Add the language option to the language selector UI
 */

import { EN } from './EN';
import { DE } from './DE';
import { FR } from './FR';
import { IT } from './IT';
import { ES } from './ES';
import { PT } from './PT';
import { RU } from './RU';
import { PL } from './PL';
import { NL } from './NL';
import { TR } from './TR';
import { EL } from './EL';
import { CS } from './CS';
import { HU } from './HU';
import { RO } from './RO';
import { BG } from './BG';
import { HR } from './HR';
import { SL } from './SL';
import { SK } from './SK';
import { SV } from './SV';
import { NO } from './NO';
import { DA } from './DA';
import { FI } from './FI';
import { ZH } from './ZH';
import { HI } from './HI';
import { AR } from './AR';
import { JA } from './JA';
import { KO } from './KO';
import { VI } from './VI';
import { ID } from './ID';
import { MS } from './MS';
import { BN } from './BN';
import { TA } from './TA';
import { TE } from './TE';
import { MR } from './MR';
import { GSW } from './GSW';
import { WAE } from './WAE';

// Language registry - all supported languages including Swiss/Valais German dialects
const languages = {
  en: EN,
  de: DE,
  fr: FR,
  it: IT,
  es: ES,
  pt: PT,
  ru: RU,
  pl: PL,
  nl: NL,
  tr: TR,
  el: EL,
  cs: CS,
  hu: HU,
  ro: RO,
  bg: BG,
  hr: HR,
  sl: SL,
  sk: SK,
  sv: SV,
  no: NO,
  da: DA,
  fi: FI,
  zh: ZH,
  hi: HI,
  ar: AR,
  ja: JA,
  ko: KO,
  vi: VI,
  id: ID,
  ms: MS,
  bn: BN,
  ta: TA,
  te: TE,
  mr: MR,
  gsw: GSW,
  wae: WAE,
};

// Default language fallback
const DEFAULT_LANGUAGE = 'en';

/**
 * Get translation for a specific key
 * @param {string} key - The translation key
 * @param {string} language - The language code (e.g., 'en', 'de')
 * @returns {string} - The translated string or the key if not found
 */
export const translate = (key, language = DEFAULT_LANGUAGE) => {
  const lang = languages[language] || languages[DEFAULT_LANGUAGE];
  return lang?.[key] ?? languages[DEFAULT_LANGUAGE]?.[key] ?? key;
};

/**
 * Get all available languages
 * @returns {Array} - Array of language objects with code and name
 */
export const getAvailableLanguages = () => [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'gsw', name: 'Swiss German', nativeName: 'Schweizerdeutsch', searchTerms: 'swiss german schwiizerdütsch alemannic' },
  { code: 'wae', name: 'Valais German', nativeName: 'Walliserdeutsch', searchTerms: 'valais wallis wallisertitsch' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe' },
  { code: 'el', name: 'Greek', nativeName: 'Ελληνικά' },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština' },
  { code: 'hu', name: 'Hungarian', nativeName: 'Magyar' },
  { code: 'ro', name: 'Romanian', nativeName: 'Română' },
  { code: 'bg', name: 'Bulgarian', nativeName: 'Български' },
  { code: 'hr', name: 'Croatian', nativeName: 'Hrvatski' },
  { code: 'sl', name: 'Slovenian', nativeName: 'Slovenščina (Slovenian)' },
  { code: 'sk', name: 'Slovak', nativeName: 'Slovenčina (Slovak)' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska' },
  { code: 'no', name: 'Norwegian', nativeName: 'Norsk' },
  { code: 'da', name: 'Danish', nativeName: 'Dansk' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi' },
  { code: 'zh', name: 'Chinese', nativeName: '中文' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
  { code: 'ko', name: 'Korean', nativeName: '한국어' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia' },
  { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు' },
  { code: 'mr', name: 'Marathi', nativeName: 'मराठी' },
];

/**
 * Check if a language is supported
 * @param {string} languageCode - The language code to check
 * @returns {boolean} - Whether the language is supported
 */
export const isLanguageSupported = (languageCode) => {
  return Object.keys(languages).includes(languageCode);
};

/**
 * Get the language object for a specific language code
 * @param {string} languageCode - The language code
 * @returns {Object} - The language object with all translations
 */
export const getLanguage = (languageCode) => {
  return languages[languageCode] || languages[DEFAULT_LANGUAGE];
};

// Export individual languages for backward compatibility during migration
export { EN, DE, FR, IT, ES, PT, RU, PL, NL, TR, EL, CS, HU, RO, BG, HR, SL, SK, SV, NO, DA, FI, ZH, HI, AR, JA, KO, VI, ID, MS, BN, TA, TE, MR, GSW, WAE };

// Export default language constant
export { DEFAULT_LANGUAGE };

